import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  Calendar, 
  Activity,
  BarChart3,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Star,
  Clock,
  Sliders,
  RotateCcw,
  Check
} from 'lucide-react';
import bookingService from '../services/bookingService';
import eventService from '../services/eventService';
import costService from '../services/costService';

const styles = `
* {
  box-sizing: border-box;
}

:root {
  --primary-gradient: linear-gradient(135deg, #2c5f6f 0%, #1a3d47 100%);
  --success-gradient: linear-gradient(135deg, #10b981 0%, #059669 100%);
  --warning-gradient: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  --danger-gradient: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  --purple-gradient: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  --blue-gradient: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 30px rgba(0, 0, 0, 0.12);
  --shadow-xl: 0 20px 40px rgba(0, 0, 0, 0.15);
  
  --radius-sm: 0.5rem;
  --radius-md: 0.75rem;
  --radius-lg: 1rem;
  --radius-xl: 1.5rem;
}

.analytics-page {
  min-height: 100vh;
  background: linear-gradient(to bottom right, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%);
  padding: 2rem 0;
}

.analytics-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.analytics-header {
  background: var(--primary-gradient);
  color: white;
  padding: 3rem 2.5rem;
  border-radius: var(--radius-xl);
  margin-bottom: 2.5rem;
  box-shadow: var(--shadow-xl);
  position: relative;
  overflow: hidden;
}

.analytics-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.analytics-header::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -5%;
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
  border-radius: 50%;
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.header-left {
  flex: 1;
  min-width: 300px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateX(-5px);
}

.analytics-title {
  font-size: 2.8rem;
  margin: 0 0 0.75rem 0;
  font-weight: 800;
  letter-spacing: -1px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.analytics-subtitle {
  font-size: 1.15rem;
  margin: 0;
  opacity: 0.95;
  font-weight: 400;
}

.filter-section {
  margin-bottom: 2rem;
  background: white;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid rgba(44, 95, 111, 0.08);
  overflow: hidden;
  position: relative;
  animation: slideDown 0.4s ease-out;
}

.filter-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--primary-gradient);
}

.filter-header {
  padding: 1.5rem 2rem 1rem;
  background: linear-gradient(to bottom, #f8fafc 0%, white 100%);
  border-bottom: 1px solid #e2e8f0;
}

.filter-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-title-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  border-radius: var(--radius-sm);
  color: #2c5f6f;
}

.filter-description {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

.filter-content {
  padding: 2rem;
}

.filter-mode-toggle {
  display: flex;
  gap: 0;
  background: #f1f5f9;
  padding: 0.375rem;
  border-radius: var(--radius-md);
  border: 1px solid #e2e8f0;
}

.mode-option {
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: none;
  border-radius: calc(var(--radius-md) - 0.375rem);
  font-weight: 600;
  font-size: 0.95rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mode-option:hover {
  color: #2c5f6f;
  background: rgba(44, 95, 111, 0.05);
}

.mode-option.active {
  background: white;
  color: #2c5f6f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-weight: 700;
}

.filter-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #e2e8f0, transparent);
  margin: 1.5rem 0;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  animation: slideDown 0.4s ease-out;
  animation-fill-mode: both;
}

.filter-group:nth-child(1) { animation-delay: 0.05s; }
.filter-group:nth-child(2) { animation-delay: 0.1s; }
.filter-group:nth-child(3) { animation-delay: 0.15s; }

.filter-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2c5f6f;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-label-icon {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  border-radius: 4px;
  color: #2c5f6f;
}

.filter-select {
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  background: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%232c5f6f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 20px;
  padding-right: 3rem;
}

.filter-select:hover {
  border-color: #2c5f6f;
  background-color: #f8fafc;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 95, 111, 0.1);
}

.filter-select:focus {
  outline: none;
  border-color: #2c5f6f;
  box-shadow: 0 0 0 4px rgba(44, 95, 111, 0.1);
  transform: translateY(-2px);
}

.filter-select:active {
  transform: translateY(0);
}

.filter-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.filter-actions-left {
  display: flex;
  gap: 0.75rem;
  flex: 1;
}

.filter-actions-right {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-btn {
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.625rem;
  position: relative;
  overflow: hidden;
}

.filter-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.filter-btn:hover::before {
  width: 300px;
  height: 300px;
}

.filter-btn-primary {
  background: var(--success-gradient);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.filter-btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
}

.filter-btn-primary:active {
  transform: translateY(-1px);
}

.filter-btn-secondary {
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.filter-btn-secondary:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.filter-btn-secondary:active {
  transform: translateY(0);
}

.period-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  color: #2c5f6f;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(44, 95, 111, 0.15);
  border: 1px solid rgba(44, 95, 111, 0.1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.stat-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: var(--secondary-gradient);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.stat-card:hover::before {
  transform: scaleX(1);
}

.stat-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-lg);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  color: #2c5f6f;
  box-shadow: 0 4px 12px rgba(44, 95, 111, 0.15);
}

.stat-icon.revenue {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #059669;
}

.stat-icon.users {
  background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%);
  color: #7c3aed;
}

.stat-icon.bookings {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.stat-icon.rate {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-value {
  font-size: 2.75rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  line-height: 1;
  letter-spacing: -1px;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 0.5rem;
}

.stat-trend.positive {
  color: #059669;
}

.stat-trend.negative {
  color: #dc2626;
}

.stat-trend.neutral {
  color: #64748b;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  font-weight: 700;
}

.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
  margin-bottom: 2.5rem;
}

.chart-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.chart-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.chart-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  color: #2c5f6f;
}

.chart-body {
  min-height: 300px;
}

.bar-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  height: 300px;
  padding: 1rem 0;
}

.bar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.bar {
  width: 100%;
  background: var(--success-gradient);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 20px;
}

.bar:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
}

.bar-value {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 700;
  color: #0f172a;
  font-size: 0.9rem;
  white-space: nowrap;
}

.bar-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  text-align: center;
}

.services-table {
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  margin-bottom: 2.5rem;
}

.table-header {
  background: var(--primary-gradient);
  color: white;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.table-content {
  padding: 2rem;
}

.service-item {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr;
  gap: 2rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
  transition: all 0.3s ease;
}

.service-item:last-child {
  border-bottom: none;
}

.service-item:hover {
  background: #f8fafc;
  transform: translateX(5px);
}

.service-name {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.service-rank {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
}

.service-rank.gold {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.service-rank.silver {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #64748b;
}

.service-rank.bronze {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #ea580c;
}

.service-rank.other {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #6366f1;
}

.service-info {
  flex: 1;
}

.service-title {
  font-weight: 700;
  color: #0f172a;
  font-size: 1.1rem;
  margin: 0 0 0.25rem 0;
}

.service-type {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
}

.service-stat {
  text-align: center;
}

.stat-value-large {
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.25rem 0;
}

.stat-label-small {
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: var(--success-gradient);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.insights-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.insight-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border-left: 4px solid #10b981;
  transition: all 0.3s ease;
}

.insight-card:hover {
  transform: translateX(5px);
  box-shadow: var(--shadow-lg);
}

.insight-card.warning {
  border-left-color: #f59e0b;
}

.insight-card.info {
  border-left-color: #3b82f6;
}

.insight-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.insight-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #059669;
}

.insight-icon.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.insight-icon.info {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.insight-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.insight-description {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
}

.empty-state-icon {
  margin: 0 auto 1rem;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c5f6f;
}

.loading-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 2rem 1.5rem;
}

.loading-header {
  background: var(--primary-gradient);
  color: white;
  padding: 3rem 2.5rem;
  border-radius: var(--radius-xl);
  margin-bottom: 2.5rem;
  box-shadow: var(--shadow-xl);
  position: relative;
  overflow: hidden;
}

.loading-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.loading-title-wrapper {
  position: relative;
  z-index: 1;
}

.loading-title {
  font-size: 2.8rem;
  margin: 0 0 0.75rem 0;
  font-weight: 800;
  letter-spacing: -1px;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-subtitle {
  font-size: 1.15rem;
  margin: 0;
  opacity: 0.95;
  font-weight: 400;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.95; }
  50% { opacity: 0.7; }
}

.loading-progress {
  margin-top: 2rem;
  text-align: center;
}

.progress-bar-container {
  width: 100%;
  max-width: 600px;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  margin: 1rem auto;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffffff, rgba(255, 255, 255, 0.7));
  border-radius: 3px;
  animation: progressAnimation 2s ease-in-out infinite;
}

@keyframes progressAnimation {
  0% { width: 0%; opacity: 0.5; }
  50% { width: 70%; opacity: 1; }
  100% { width: 100%; opacity: 0.5; }
}

.loading-steps {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.loading-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  animation: fadeInOut 2s ease-in-out infinite;
}

.loading-step:nth-child(1) { animation-delay: 0s; }
.loading-step:nth-child(2) { animation-delay: 0.5s; }
.loading-step:nth-child(3) { animation-delay: 1s; }

@keyframes fadeInOut {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.loading-step-icon {
  width: 16px;
  height: 16px;
  border: 2px solid white;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.skeleton-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.skeleton-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.skeleton-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, #e6f4f4 0%, #d1e9e9 100%);
  margin-bottom: 1rem;
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

.skeleton-text {
  height: 14px;
  background: #e2e8f0;
  border-radius: 4px;
  margin-bottom: 0.75rem;
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

.skeleton-text.title {
  height: 40px;
  width: 60%;
  margin-bottom: 1rem;
}

.skeleton-text.small {
  width: 40%;
  height: 12px;
}

@keyframes skeletonPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.skeleton-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
  margin-bottom: 2.5rem;
}

.skeleton-chart {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(0, 0, 0, 0.05);
  height: 400px;
  position: relative;
  overflow: hidden;
}

.skeleton-chart::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

.skeleton-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  height: 250px;
  margin-top: 2rem;
}

.skeleton-bar {
  flex: 1;
  background: linear-gradient(to top, #e2e8f0, #f1f5f9);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  animation: barGrow 1.5s ease-in-out infinite;
}

@keyframes barGrow {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.8); }
}

.skeleton-bar:nth-child(1) { height: 60%; animation-delay: 0s; }
.skeleton-bar:nth-child(2) { height: 80%; animation-delay: 0.2s; }
.skeleton-bar:nth-child(3) { height: 70%; animation-delay: 0.4s; }
.skeleton-bar:nth-child(4) { height: 90%; animation-delay: 0.6s; }
.skeleton-bar:nth-child(5) { height: 75%; animation-delay: 0.8s; }
.skeleton-bar:nth-child(6) { height: 65%; animation-delay: 1s; }

@media (max-width: 1024px) {
  .charts-section {
    grid-template-columns: 1fr;
  }
  
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .skeleton-charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .analytics-title {
    font-size: 2rem;
  }
  
  .dashboard-stats {
    grid-template-columns: 1fr;
  }
  
  .service-item {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .insights-section {
    grid-template-columns: 1fr;
  }
  
  .filter-content {
    padding: 1.5rem;
  }
  
  .filter-header {
    padding: 1.25rem 1.5rem 0.75rem;
  }
  
  .filter-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filter-actions-left,
  .filter-actions-right {
    width: 100%;
    flex-direction: column;
  }
  
  .filter-btn {
    width: 100%;
    justify-content: center;
  }
  
  .filter-mode-toggle {
    flex-direction: column;
  }
  
  .mode-option {
    text-align: center;
  }
  
  .loading-steps {
    flex-direction: column;
    align-items: center;
  }
  
  .skeleton-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .filter-select {
    font-size: 0.9rem;
    padding: 0.75rem 0.875rem;
    padding-right: 2.5rem;
    background-size: 18px;
  }
  
  .filter-btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
  }
}
`;

export default function AnalyticsPage() {
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState('month');
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    totalUsers: 0,
    avgBookingValue: 0,
    revenueGrowth: 0,
    bookingsGrowth: 0,
    topServices: [],
    monthlyRevenue: [],
    conversionRate: 0
  });

  const [costs, setCosts] = useState({
    eventCosts: [],
    serviceCosts: [],
    bookingCosts: []
  });
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterMode, setFilterMode] = useState('period');
  
  const [tempMonth, setTempMonth] = useState(new Date().getMonth());
  const [tempYear, setTempYear] = useState(new Date().getFullYear());
  const [tempPeriod, setTempPeriod] = useState('month');
  const [tempFilterMode, setTempFilterMode] = useState('period');

  useEffect(() => {
    loadData();
  }, [navigate]);

  useEffect(() => {
    if (!loading) {
      calculateAnalytics(bookings, events, costs);
    }
  }, [period, selectedMonth, selectedYear, filterMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsData, eventsData, costsData] = await Promise.all([
        bookingService.getAllBookings(),
        eventService.getAllEventsAdmin(),
        costService.getAllCosts()
      ]);

      setBookings(bookingsData);
      setEvents(eventsData);
      setCosts(costsData);
      calculateAnalytics(bookingsData, eventsData, costsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthsList = () => {
    return [
      { value: 0, label: 'Janeiro' },
      { value: 1, label: 'Fevereiro' },
      { value: 2, label: 'Março' },
      { value: 3, label: 'Abril' },
      { value: 4, label: 'Maio' },
      { value: 5, label: 'Junho' },
      { value: 6, label: 'Julho' },
      { value: 7, label: 'Agosto' },
      { value: 8, label: 'Setembro' },
      { value: 9, label: 'Outubro' },
      { value: 10, label: 'Novembro' },
      { value: 11, label: 'Dezembro' }
    ];
  };

  const getYearsList = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const resetFilters = () => {
    const now = new Date();
    setTempFilterMode('period');
    setTempPeriod('month');
    setTempMonth(now.getMonth());
    setTempYear(now.getFullYear());
  };

  const applyFilters = () => {
    setFilterMode(tempFilterMode);
    setPeriod(tempPeriod);
    setSelectedMonth(tempMonth);
    setSelectedYear(tempYear);
  };

  const getSelectedPeriodText = () => {
    if (filterMode === 'specific') {
      const monthName = getMonthsList()[selectedMonth].label;
      return `${monthName} ${selectedYear}`;
    }
    
    switch(period) {
      case 'month':
        return 'Último Mês';
      case 'quarter':
        return 'Último Trimestre';
      case 'year':
        return 'Último Ano';
      default:
        return '';
    }
  };

  const calculateAnalytics = (bookingsData, eventsData, costsData = costs) => {
  const now = new Date();
  let periodStart, periodEnd;
  
  if (filterMode === 'specific') {
    periodStart = new Date(selectedYear, selectedMonth, 1);
    periodEnd = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);
  } else {
    periodStart = getPeriodStart(now, period);
    periodEnd = now;
  }
  
  console.log('Período:', periodStart, 'até', periodEnd);
  
  const confirmedBookings = bookingsData.filter(b => {
    const bookingDate = new Date(b.dataHora);
    return b.status === 'Confirmado' && 
           bookingDate >= periodStart && 
           bookingDate <= periodEnd;
  });

  const eventReservationsInPeriod = [];
  
  eventsData.forEach(event => {
    if (event.reservations && event.reservations.length > 0) {
      event.reservations.forEach(reservation => {
        const hasTimeSlots = reservation.timeSlots && reservation.timeSlots.length > 0;
        
        if (hasTimeSlots) {
          reservation.timeSlots.forEach(timeSlot => {
            const timeSlotDate = new Date(timeSlot.specificDate);
            
            if (timeSlotDate >= periodStart && timeSlotDate <= periodEnd) {
              eventReservationsInPeriod.push({
                ...reservation,
                eventId: event.id,
                eventTitle: event.title,
                eventPrice: parseFloat(timeSlot.price) || 0,
                eventDate: timeSlot.specificDate,
                isTimeSlotBased: true,
                timeSlotName: timeSlot.timeSlotName || 'N/A',
                timeSlotReservations: reservation.timeSlotReservations || []
              });
            }
          });
        } else {
          const reservationDate = new Date(event.startDate);
          
          if (reservationDate >= periodStart && reservationDate <= periodEnd) {
            eventReservationsInPeriod.push({
              ...reservation,
              eventId: event.id,
              eventTitle: event.title,
              eventPrice: parseFloat(event.price) || 0,
              eventDate: event.startDate,
              isTimeSlotBased: false
            });
          }
        }
      });
    }
  });

  console.log('Bookings no período:', confirmedBookings.length);
  console.log('Reservas de eventos no período:', eventReservationsInPeriod.length);

  const bookingsRevenue = confirmedBookings.reduce((sum, booking) => {
    const price = parseFloat(booking.preco.replace('€', '').replace(',', '.')) || 0;
    return sum + price;
  }, 0);

  const eventRevenue = eventReservationsInPeriod.reduce((sum, reservation) => {
    const price = reservation.eventPrice;
    const participants = reservation.numberOfParticipants || 1;
    return sum + (price * participants);
  }, 0);

  const totalRevenue = bookingsRevenue + eventRevenue;
  const totalReservations = confirmedBookings.length + eventReservationsInPeriod.length;

  let totalCosts = 0;

  const bookingCostsByBookingId = {};
  
  costsData.bookingCosts?.forEach(bookingCost => {
    if (!bookingCostsByBookingId[bookingCost.bookingId]) {
      bookingCostsByBookingId[bookingCost.bookingId] = [];
    }
    bookingCostsByBookingId[bookingCost.bookingId].push(bookingCost);
  });

  console.log('Custos agrupados por booking:', bookingCostsByBookingId);

  confirmedBookings.forEach(booking => {
    const bookingSpecificCosts = bookingCostsByBookingId[booking.id];
    
    if (bookingSpecificCosts && bookingSpecificCosts.length > 0) {
      const totalBookingCost = bookingSpecificCosts.reduce((sum, cost) => sum + cost.cost, 0);
      totalCosts += totalBookingCost;
      console.log(`Custos específicos para booking ${booking.id}: ${bookingSpecificCosts.length} custos, total = ${totalBookingCost}€`);
    } else {
      const serviceCost = costsData.serviceCosts?.find(sc => 
        sc.serviceType === booking.servicoTipo
      );
      
      if (serviceCost) {
        totalCosts += serviceCost.defaultCost;
        console.log(`Custo padrão para ${booking.servicoTipo}: ${serviceCost.defaultCost}€`);
      } else {
        console.log(`Nenhum custo definido para ${booking.servicoTipo}`);
      }
    }
  });

  eventReservationsInPeriod.forEach(reservation => {
    const eventId = reservation.eventId;
    
    if (reservation.isTimeSlotBased && reservation.timeSlotReservations) {
      reservation.timeSlotReservations.forEach(timeSlotRes => {
        const specificDate = timeSlotRes.specificDate;
        
        if (specificDate) {
          const eventCost = costsData.eventCosts?.find(ec => 
            ec.eventId === eventId && 
            ec.specificDate && 
            new Date(ec.specificDate).toDateString() === new Date(specificDate).toDateString()
          );
          
          if (eventCost) {
            totalCosts += eventCost.cost;
            console.log(`Custo específico para evento ${eventId} na data ${specificDate}: ${eventCost.cost}€`);
          } else {
            const defaultEventCost = costsData.eventCosts?.find(ec => 
              ec.eventId === eventId && !ec.specificDate
            );
            if (defaultEventCost) {
              totalCosts += defaultEventCost.cost;
              console.log(`Custo padrão para evento ${eventId}: ${defaultEventCost.cost}€`);
            } else {
              console.log(`Nenhum custo definido para evento ${eventId}`);
            }
          }
        }
      });
    } else {
      const eventDate = new Date(reservation.eventDate);
      
      const eventCost = costsData.eventCosts?.find(ec => 
        ec.eventId === eventId && 
        ec.specificDate && 
        new Date(ec.specificDate).toDateString() === eventDate.toDateString()
      );
      
      if (eventCost) {
        totalCosts += eventCost.cost;
        console.log(`Custo específico para evento ${eventId} na data ${eventDate}: ${eventCost.cost}€`);
      } else {
        const defaultEventCost = costsData.eventCosts?.find(ec => 
          ec.eventId === eventId && !ec.specificDate
        );
        if (defaultEventCost) {
          totalCosts += defaultEventCost.cost;
          console.log(`Custo padrão para evento ${eventId}: ${defaultEventCost.cost}€`);
        } else {
          console.log(`Nenhum custo definido para evento ${eventId}`);
        }
      }
    }
  });

  const netProfit = totalRevenue - totalCosts;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  console.log('=== RESUMO FINAL ===');
  console.log('Receita total:', totalRevenue);
  console.log('Custos totais:', totalCosts);
  console.log('Lucro líquido:', netProfit);
  console.log('Margem de lucro:', profitMargin.toFixed(2) + '%');


  const allUserIds = [
    ...confirmedBookings.map(b => b.userId),
    ...eventReservationsInPeriod.map(r => r.userId)
  ];
  const uniqueUsers = new Set(allUserIds.filter(id => id != null)).size;

  const avgBookingValue = totalReservations > 0 
    ? totalRevenue / totalReservations 
    : 0;

  let previousPeriodStart, previousPeriodEnd;
  
  if (filterMode === 'specific') {
    previousPeriodStart = new Date(selectedYear, selectedMonth - 1, 1);
    previousPeriodEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);
  } else {
    previousPeriodEnd = periodStart;
    previousPeriodStart = getPeriodStart(periodStart, period);
  }

  const previousBookings = bookingsData.filter(b => {
    const bookingDate = new Date(b.dataHora);
    return b.status === 'Confirmado' && 
           bookingDate >= previousPeriodStart && 
           bookingDate < previousPeriodEnd;
  });

  const previousEventReservations = [];
  
  eventsData.forEach(event => {
    if (event.reservations && event.reservations.length > 0) {
      event.reservations.forEach(reservation => {
        const hasTimeSlots = reservation.timeSlots && reservation.timeSlots.length > 0;
        
        if (hasTimeSlots) {
          reservation.timeSlots.forEach(timeSlot => {
            const timeSlotDate = new Date(timeSlot.specificDate);
            
            if (timeSlotDate >= previousPeriodStart && timeSlotDate < previousPeriodEnd) {
              previousEventReservations.push({
                ...reservation,
                eventPrice: parseFloat(timeSlot.price) || 0,
                isTimeSlotBased: true
              });
            }
          });
        } else {
          const reservationDate = new Date(event.startDate);
          
          if (reservationDate >= previousPeriodStart && reservationDate < previousPeriodEnd) {
            previousEventReservations.push({
              ...reservation,
              eventPrice: parseFloat(event.price) || 0,
              isTimeSlotBased: false
            });
          }
        }
      });
    }
  });

  const previousBookingsRevenue = previousBookings.reduce((sum, booking) => {
    const price = parseFloat(booking.preco.replace('€', '').replace(',', '.')) || 0;
    return sum + price;
  }, 0);

  const previousEventsRevenue = previousEventReservations.reduce((sum, reservation) => {
    const price = reservation.eventPrice;
    const participants = reservation.numberOfParticipants || 1;
    return sum + (price * participants);
  }, 0);

  const previousRevenue = previousBookingsRevenue + previousEventsRevenue;
  const previousTotalReservations = previousBookings.length + previousEventReservations.length;

  const revenueGrowth = previousRevenue > 0 
    ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
    : 0;

  const bookingsGrowth = previousTotalReservations > 0 
    ? ((totalReservations - previousTotalReservations) / previousTotalReservations) * 100 
    : 0;

  const serviceStats = {};
  
  confirmedBookings.forEach(booking => {
    const serviceName = booking.servicoNome;
    if (!serviceStats[serviceName]) {
      serviceStats[serviceName] = {
        name: serviceName,
        type: booking.servicoTipo || 'Serviço',
        count: 0,
        revenue: 0
      };
    }
    serviceStats[serviceName].count++;
    const price = parseFloat(booking.preco.replace('€', '').replace(',', '.')) || 0;
    serviceStats[serviceName].revenue += price;
  });

  const eventGroups = {};
  eventReservationsInPeriod.forEach(reservation => {
    const serviceName = reservation.eventTitle;
    
    if (!eventGroups[serviceName]) {
      eventGroups[serviceName] = {
        name: serviceName,
        type: 'Evento',
        items: [],
        revenue: 0
      };
    }
    
    const price = reservation.eventPrice * (reservation.numberOfParticipants || 1);
    eventGroups[serviceName].revenue += price;
    eventGroups[serviceName].items.push(reservation);
  });

  Object.values(eventGroups).forEach(group => {
    if (!serviceStats[group.name]) {
      serviceStats[group.name] = {
        name: group.name,
        type: group.type,
        count: group.items.length,
        revenue: group.revenue
      };
    }
  });

  const topServices = Object.values(serviceStats)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const monthlyRevenue = calculateMonthlyRevenue(confirmedBookings, eventsData, periodStart);

  const totalPossibleReservations = bookingsData.length + eventsData.reduce((sum, event) => {
    return sum + (event.reservations ? event.reservations.length : 0);
  }, 0);
  
  const conversionRate = totalPossibleReservations > 0
    ? (totalReservations / totalPossibleReservations) * 100
    : 0;

  setAnalytics({
    totalRevenue: totalRevenue,
    totalBookings: totalReservations,
    totalUsers: uniqueUsers,
    avgBookingValue: avgBookingValue,
    revenueGrowth: revenueGrowth,
    bookingsGrowth: bookingsGrowth,
    topServices: topServices,
    monthlyRevenue: monthlyRevenue,
    conversionRate: conversionRate,
    totalCosts: totalCosts,
    netProfit: netProfit,
    profitMargin: profitMargin
  });
};

  const getPeriodStart = (date, periodType) => {
    if (filterMode === 'specific') {
      return new Date(selectedYear, selectedMonth, 1);
    }
    
    const d = new Date(date);
    if (periodType === 'month') {
      d.setMonth(d.getMonth() - 1);
    } else if (periodType === 'quarter') {
      d.setMonth(d.getMonth() - 3);
    } else if (periodType === 'year') {
      d.setFullYear(d.getFullYear() - 1);
    }
    return d;
  };

  const calculateMonthlyRevenue = (bookings, events, startDate) => {
    const months = {};
    const now = new Date();
    
    bookings.forEach(booking => {
      const bookingDate = new Date(booking.dataHora);
      const monthKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (!months[monthKey]) {
        months[monthKey] = 0;
      }
      
      const price = parseFloat(booking.preco.replace('€', '').replace(',', '.')) || 0;
      months[monthKey] += price;
    });

    events.forEach(event => {
      if (event.reservations && event.reservations.length > 0) {
        event.reservations.forEach(reservation => {
          const hasTimeSlots = reservation.timeSlots && reservation.timeSlots.length > 0;
          
          if (hasTimeSlots) {
            reservation.timeSlots.forEach(timeSlot => {
              const timeSlotDate = new Date(timeSlot.specificDate);
              const monthKey = `${timeSlotDate.getFullYear()}-${String(timeSlotDate.getMonth() + 1).padStart(2, '0')}`;
              
              if (!months[monthKey]) {
                months[monthKey] = 0;
              }
              
              const price = parseFloat(timeSlot.price) || 0;
              const participants = reservation.numberOfParticipants || 1;
              months[monthKey] += price * participants;
            });
          } else {
            const eventDate = new Date(event.startDate);
            const monthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
            
            if (!months[monthKey]) {
              months[monthKey] = 0;
            }
            
            const eventPrice = parseFloat(event.price) || 0;
            const participants = reservation.numberOfParticipants || 1;
            months[monthKey] += eventPrice * participants;
          }
        });
      }
    });

    const result = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthName = d.toLocaleDateString('pt-PT', { month: 'short' });
      
      result.push({
        month: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        revenue: months[monthKey] || 0
      });
    }

    return result;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const maxMonthlyRevenue = Math.max(...analytics.monthlyRevenue.map(m => m.revenue), 1);

  if (loading) {
    return (
      <div className="analytics-page">
        <style>{styles}</style>
        <div className="analytics-container loading-container">
          <div className="loading-header">
            <div className="loading-title-wrapper">
              <h1 className="loading-title">
                <Activity size={40} className="loading-spinner" />
                A Carregar Análises
              </h1>
              <p className="loading-subtitle">
                A processar dados de reservas e eventos...
              </p>
            </div>
            
            <div className="loading-progress">
              <div className="progress-bar-container">
                <div className="progress-bar-fill"></div>
              </div>
              
              <div className="loading-steps">
                <div className="loading-step">
                  <div className="loading-step-icon"></div>
                  <span>A carregar bookings</span>
                </div>
                <div className="loading-step">
                  <div className="loading-step-icon"></div>
                  <span>A carregar eventos</span>
                </div>
                <div className="loading-step">
                  <div className="loading-step-icon"></div>
                  <span>A calcular métricas</span>
                </div>
              </div>
            </div>
          </div>

          <div className="skeleton-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-icon"></div>
                <div className="skeleton-text small"></div>
                <div className="skeleton-text title"></div>
                <div className="skeleton-text small"></div>
              </div>
            ))}
          </div>

          <div className="skeleton-charts">
            {[1, 2].map((i) => (
              <div key={i} className="skeleton-chart">
                <div className="skeleton-text" style={{ width: '40%', marginBottom: '2rem' }}></div>
                <div className="skeleton-bars">
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <div key={j} className="skeleton-bar"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <style>{styles}</style>
      <div className="analytics-container">
        <div className="analytics-header">
          <div className="header-content">
            <div className="header-left">
              <button className="back-button" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft size={20} />
                Voltar ao Dashboard
              </button>
              <h1 className="analytics-title">
                <Activity size={40} />
                Análise de Desempenho
              </h1>
              <p className="analytics-subtitle">
                Métricas e insights detalhados do seu negócio
              </p>
            </div>
          </div>
        </div>

        <div className="filter-section">
          <div className="filter-header">
            <div className="filter-title">
              <div className="filter-title-icon">
                <Sliders size={18} />
              </div>
              Filtros de Análise
            </div>
            <p className="filter-description">
              Personalize a visualização das suas métricas e relatórios
            </p>
          </div>

          <div className="filter-content">
            <div className="filter-mode-toggle">
              <button 
                className={`mode-option ${tempFilterMode === 'period' ? 'active' : ''}`}
                onClick={() => setTempFilterMode('period')}
              >
                <Clock size={18} />
                Período Relativo
              </button>
              <button 
                className={`mode-option ${tempFilterMode === 'specific' ? 'active' : ''}`}
                onClick={() => setTempFilterMode('specific')}
              >
                <Calendar size={18} />
                Mês Específico
              </button>
            </div>

            <div className="filter-divider"></div>

            <div className="filter-grid">
              {tempFilterMode === 'period' ? (
                <div className="filter-group">
                  <label className="filter-label">
                    <div className="filter-label-icon">
                      <TrendingUp size={14} />
                    </div>
                    Período
                  </label>
                  <select 
                    className="filter-select"
                    value={tempPeriod}
                    onChange={(e) => setTempPeriod(e.target.value)}
                  >
                    <option value="month">Último Mês</option>
                    <option value="quarter">Último Trimestre</option>
                    <option value="year">Último Ano</option>
                  </select>
                </div>
              ) : (
                <>
                  <div className="filter-group">
                    <label className="filter-label">
                      <div className="filter-label-icon">
                        <Calendar size={14} />
                      </div>
                      Mês
                    </label>
                    <select 
                      className="filter-select"
                      value={tempMonth}
                      onChange={(e) => setTempMonth(parseInt(e.target.value))}
                    >
                      {getMonthsList().map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <div className="filter-label-icon">
                        <Calendar size={14} />
                      </div>
                      Ano
                    </label>
                    <select 
                      className="filter-select"
                      value={tempYear}
                      onChange={(e) => setTempYear(parseInt(e.target.value))}
                    >
                      {getYearsList().map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="filter-actions">
              <div className="filter-actions-left">
                <button 
                  className="filter-btn filter-btn-secondary"
                  onClick={resetFilters}
                  title="Resetar todos os filtros"
                >
                  <RotateCcw size={18} />
                  Resetar
                </button>
              </div>
              
              <div className="filter-actions-right">
                <button 
                  className="filter-btn filter-btn-primary"
                  onClick={applyFilters}
                  title="Aplicar filtros selecionados"
                >
                  <Check size={18} />
                  Aplicar Filtros
                </button>
                
                <div className="period-badge">
                  <Calendar size={18} />
                  {getSelectedPeriodText()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon revenue">
                <DollarSign size={28} />
              </div>
            </div>
            <div className="stat-content">
              <p className="stat-label">Faturação Total</p>
              <h2 className="stat-value">{formatCurrency(analytics.totalRevenue)}</h2>
              <div className={`stat-trend ${analytics.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                {analytics.revenueGrowth >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                {Math.abs(analytics.revenueGrowth).toFixed(1)}% vs período anterior
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon bookings">
                <Calendar size={28} />
              </div>
            </div>
            <div className="stat-content">
              <p className="stat-label">Reservas Confirmadas</p>
              <h2 className="stat-value">{analytics.totalBookings}</h2>
              <div className={`stat-trend ${analytics.bookingsGrowth >= 0 ? 'positive' : 'negative'}`}>
                {analytics.bookingsGrowth >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                {Math.abs(analytics.bookingsGrowth).toFixed(1)}% vs período anterior
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon users">
                <Users size={28} />
              </div>
            </div>
            <div className="stat-content">
              <p className="stat-label">Clientes Ativos</p>
              <h2 className="stat-value">{analytics.totalUsers}</h2>
              <div className="stat-trend neutral">
                <Activity size={16} />
                Clientes únicos no período
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', color: '#dc2626' }}>
                <TrendingDown size={28} />
              </div>
            </div>
            <div className="stat-content">
              <p className="stat-label">Custos Totais</p>
              <h2 className="stat-value">{formatCurrency(analytics.totalCosts || 0)}</h2>
              <div className="stat-trend neutral">
                <Activity size={16} />
                Custos operacionais do período
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', color: '#059669' }}>
                  <DollarSign size={28} />
                </div>
              </div>
              <div className="stat-content">
                <p className="stat-label">Lucro Líquido</p>
                <h2 className="stat-value">{formatCurrency(analytics.netProfit || 0)}</h2>
                <div className={`stat-trend ${(analytics.profitMargin || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(analytics.profitMargin || 0) >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {Math.abs(analytics.profitMargin || 0).toFixed(1)}% Margem de lucro
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="charts-section">
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title">
                <div className="chart-icon">
                  <BarChart3 size={20} />
                </div>
                Faturação Mensal
              </div>
            </div>
            <div className="chart-body">
              <div className="bar-chart">
                {analytics.monthlyRevenue.map((data, index) => (
                  <div key={index} className="bar-item">
                    <div 
                      className="bar" 
                      style={{ height: `${(data.revenue / maxMonthlyRevenue) * 100}%` }}
                    >
                      <span className="bar-value">{formatCurrency(data.revenue)}</span>
                    </div>
                    <span className="bar-label">{data.month}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {analytics.topServices.length > 0 && (
          <div className="services-table">
            <div className="table-header">
              <div className="table-title">
                <Star size={24} />
                Serviços Mais Populares
              </div>
            </div>
            <div className="table-content">
              {analytics.topServices.map((service, index) => {
                const maxRevenue = Math.max(...analytics.topServices.map(s => s.revenue), 1);
                const percentage = (service.revenue / maxRevenue) * 100;
                
                return (
                  <div key={index} className="service-item">
                    <div className="service-name">
                      <div className={`service-rank ${
                        index === 0 ? 'gold' : 
                        index === 1 ? 'silver' : 
                        index === 2 ? 'bronze' : 'other'
                      }`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </div>
                      <div className="service-info">
                        <h3 className="service-title">{service.name}</h3>
                        <p className="service-type">{service.type}</p>
                      </div>
                    </div>
                    
                    <div className="service-stat">
                      <div className="stat-value-large">{service.count}</div>
                      <div className="stat-label-small">Reservas</div>
                    </div>
                    
                    <div className="service-stat">
                      <div className="stat-value-large">{formatCurrency(service.revenue)}</div>
                      <div className="stat-label-small">Faturação</div>
                    </div>
                    
                    <div style={{ width: '100%' }}>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}